/**
 * Hook pour filtrer, rechercher et paginer les participants
 */
import { useState, useMemo, useCallback } from 'react';

const PARTICIPANTS_PER_PAGE = 15;

const useParticipantFiltering = ({
  users = [],
  userAvailability = {},
  currentParticipantIds = new Set()
}) => {
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [page, setPage] = useState(0);

  // Ordre des statuts employé : CDI (1) > CDD (2) > Interim (3)
  const getEmployeeStatusOrder = useCallback((status) => {
    if (!status) return 99;
    const lower = status.toLowerCase();
    if (lower === 'cdi') return 1;
    if (lower === 'cdd') return 2;
    if (lower.includes('interim') || lower.includes('intérim')) return 3;
    return 99;
  }, []);

  // Rôles autorisés pour les participants (terrain uniquement)
  const ALLOWED_ROLES = ["Chef d'équipe", "Employés"];

  // Filtrage et tri optimisé
  const filteredUsers = useMemo(() => {
    // Ne montrer que les utilisateurs actifs ET avec un rôle terrain (chef d'équipe ou employé)
    let filtered = users.filter(user =>
      user.is_active !== false &&
      ALLOWED_ROLES.includes(user.role_name)
    );

    // Filtre par groupe
    if (teamFilter) {
      filtered = filtered.filter(user => user.group_ids?.includes(teamFilter) || user.group_id === teamFilter);
    }

    // Filtre par recherche
    if (search.trim()) {
      const searchLower = search.toLowerCase().trim();
      const searchTerms = searchLower.split(' ');
      filtered = filtered.filter(user => {
        const userText = `${user.full_name} ${user.group_name || ''} ${user.role_name}`.toLowerCase();
        return searchTerms.every(term => userText.includes(term));
      });
    }

    // Tri: disponibles > chef d'équipe > CDI > CDD > Interim > nom
    return filtered.sort((a, b) => {
      const availabilityA = userAvailability[a.id]?.status || 'available';
      const availabilityB = userAvailability[b.id]?.status || 'available';

      // 1. Disponibles en premier
      if (availabilityA !== availabilityB) {
        if (availabilityA === 'available') return -1;
        if (availabilityB === 'available') return 1;
      }

      // 2. Chef d'équipe en premier
      const isChefA = a.role_name === "Chef d'équipe";
      const isChefB = b.role_name === "Chef d'équipe";
      if (isChefA && !isChefB) return -1;
      if (!isChefA && isChefB) return 1;

      // 3. Par statut employé : CDI > CDD > Interim
      const statusOrderA = getEmployeeStatusOrder(a.employee_status);
      const statusOrderB = getEmployeeStatusOrder(b.employee_status);
      if (statusOrderA !== statusOrderB) return statusOrderA - statusOrderB;

      // 4. Par nom
      return (a.full_name || '').localeCompare(b.full_name || '');
    });
  }, [users, teamFilter, search, userAvailability, getEmployeeStatusOrder]);

  // Pagination
  const paginatedUsers = useMemo(() => {
    const start = page * PARTICIPANTS_PER_PAGE;
    const end = start + PARTICIPANTS_PER_PAGE;
    return {
      users: filteredUsers.slice(start, end),
      total: filteredUsers.length,
      hasMore: end < filteredUsers.length,
      hasPrev: page > 0,
      totalPages: Math.ceil(filteredUsers.length / PARTICIPANTS_PER_PAGE),
      // Compter uniquement les utilisateurs vraiment disponibles (pas hr_unavailable)
      availableCount: filteredUsers.filter(u => {
        const status = userAvailability[u.id]?.status;
        return status === 'available' || !status;
      }).length
    };
  }, [filteredUsers, page, userAvailability]);

  // Handlers
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setPage(0);
  }, []);

  const handleTeamFilterChange = useCallback((value) => {
    setTeamFilter(value);
    setPage(0);
  }, []);

  const goToPage = useCallback((newPage) => {
    setPage(Math.max(0, Math.min(newPage, paginatedUsers.totalPages - 1)));
  }, [paginatedUsers.totalPages]);

  const nextPage = useCallback(() => {
    if (paginatedUsers.hasMore) setPage(p => p + 1);
  }, [paginatedUsers.hasMore]);

  const prevPage = useCallback(() => {
    if (paginatedUsers.hasPrev) setPage(p => p - 1);
  }, [paginatedUsers.hasPrev]);

  return {
    // State
    search,
    teamFilter,
    page,
    // Results
    filteredUsers,
    paginatedUsers,
    // Actions
    setSearch: handleSearchChange,
    setTeamFilter: handleTeamFilterChange,
    setPage: goToPage,
    nextPage,
    prevPage,
    // Constants
    PARTICIPANTS_PER_PAGE
  };
};

export default useParticipantFiltering;
